/**
 * Componente para compartir contenido en redes sociales
 * 
 * Permite compartir logros de ProPoints en:
 * - WhatsApp
 * - Facebook
 * - Twitter/X
 * - Copiar enlace
 */

import { useState } from 'react';
import PropTypes from 'prop-types';
import '../../styles/share-social.css';

const ShareSocialButton = ({ 
  title, 
  text, 
  url, 
  hashtags = ['LigaProKids', 'ProPoints'],
  onShare 
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = url || window.location.href;
  const shareText = text || title;

  const handleShare = (platform) => {
    let shareLink = '';

    switch (platform) {
      case 'whatsapp':
        shareLink = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;
        break;

      case 'facebook':
        // Facebook ya no soporta el parámetro 'quote'
        // Solo necesita la URL, el texto lo obtiene de los meta tags Open Graph
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;

      case 'twitter': {
        const hashtagsStr = hashtags.join(',');
        shareLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}&hashtags=${hashtagsStr}`;
        break;
      }

      case 'copy':
        // Copiar solo la URL para facilitar pegar en navegador
        navigator.clipboard.writeText(shareUrl).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
        setShowMenu(false);
        if (onShare) onShare('copy');
        return;

      default:
        return;
    }

    if (shareLink) {
      window.open(shareLink, '_blank', 'width=600,height=400');
      setShowMenu(false);
      if (onShare) onShare(platform);
    }
  };

  return (
    <div className="share-social-container">
      <button
        className="share-social-button"
        onClick={() => setShowMenu(!showMenu)}
        title="Compartir en redes sociales"
      >
        <span className="material-icons">share</span>
        <span className="share-text">Compartir</span>
      </button>

      {showMenu && (
        <>
          <div 
            className="share-social-overlay" 
            onClick={() => setShowMenu(false)}
          />
          <div className="share-social-menu">
            <div className="share-social-header">
              <h4>Compartir en:</h4>
              <button 
                className="share-social-close"
                onClick={() => setShowMenu(false)}
              >
                <span className="material-icons">close</span>
              </button>
            </div>

            <div className="share-social-options">
              <button
                className="share-option whatsapp"
                onClick={() => handleShare('whatsapp')}
              >
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path fill="currentColor" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                <span>WhatsApp</span>
              </button>

              <button
                className="share-option facebook"
                onClick={() => handleShare('facebook')}
              >
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path fill="currentColor" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span>Facebook</span>
              </button>

              <button
                className="share-option twitter"
                onClick={() => handleShare('twitter')}
              >
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                <span>Twitter/X</span>
              </button>

              <button
                className="share-option copy"
                onClick={() => handleShare('copy')}
              >
                <span className="material-icons">
                  {copied ? 'check' : 'content_copy'}
                </span>
                <span>{copied ? '¡Copiado!' : 'Copiar enlace'}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

ShareSocialButton.propTypes = {
  title: PropTypes.string,
  text: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  hashtags: PropTypes.arrayOf(PropTypes.string),
  onShare: PropTypes.func
};

export default ShareSocialButton;

